// src/components/KeyFobsTab.tsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { formatDate as formatDateShared } from "@/lib/dates";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Badge } from "@/components/ui/badge";
import { Pencil, RefreshCw, Plus, Ban, Search, X } from "lucide-react";

type FobStatus = "active" | "inactive";
type DepositPaidType = "Cash" | "EFTPOS" | "Deduction" | "";
type DepositRefundType = "Cash" | "Bank Payment" | "";

type KeyFobRow = {
  id: string;
  fob_number: string;
  driver_name: string | null;
  date_assigned: string | null;
  deposit_paid: DepositPaidType | string | null;
  returned_date: string | null;
  deposit_refund: DepositRefundType | string | null;
  status: FobStatus | string;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

type DriverOption = {
  driver_name: string;
  is_active: boolean;
};

function statusBadgeVariant(status: string) {
  if (status === "active") return "default";
  return "secondary";
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return formatDateShared(dateStr) || "-";
}

// Natural sort function - extracts numbers and sorts numerically
function naturalSort(a: string, b: string): number {
  const extractNumber = (str: string): number => {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };
  
  const numA = extractNumber(a);
  const numB = extractNumber(b);
  
  if (numA !== numB) {
    return numA - numB;
  }
  
  // If numbers are equal, fall back to string comparison
  return a.localeCompare(b);
}

const NO_DRIVER = "__none__";

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees only the fob(s) assigned to their linked driver(s), and can never
// add/edit/deactivate -- key fobs are a shared fleet resource, not
// something scoped to one driver.
const KeyFobsTab: React.FC<{ isAdmin?: boolean; scopedDriverNames?: string[] | null }> = ({ isAdmin = true, scopedDriverNames = null }) => {
  const canManage = isAdmin && !scopedDriverNames;
  const [fobs, setFobs] = useState<KeyFobRow[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "assigned" | "unassigned">("all");

  // Add new fob form
  const [addOpen, setAddOpen] = useState(false);
  const [newFob, setNewFob] = useState<{
    fob_number: string;
    driver_name: string;
    date_assigned: string;
    deposit_paid: DepositPaidType;
    returned_date: string;
    deposit_refund: DepositRefundType;
    status: FobStatus;
    notes: string;
  }>({
    fob_number: "",
    driver_name: "",
    date_assigned: "",
    deposit_paid: "",
    returned_date: "",
    deposit_refund: "",
    status: "active",
    notes: "",
  });

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<KeyFobRow | null>(null);
  const [editValues, setEditValues] = useState<{
    fob_number: string;
    driver_name: string;
    date_assigned: string;
    deposit_paid: DepositPaidType;
    returned_date: string;
    deposit_refund: DepositRefundType;
    status: FobStatus;
    notes: string;
  }>({
    fob_number: "",
    driver_name: "",
    date_assigned: "",
    deposit_paid: "",
    returned_date: "",
    deposit_refund: "",
    status: "active",
    notes: "",
  });

  const fetchFobs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("key_fobs")
        .select("*")
        .order("fob_number", { ascending: true });
      if (scopedDriverNames) {
        query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
      }
      const { data, error } = await query;

      if (error) throw error;

      setFobs(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast({
        title: "Error fetching key fobs",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from("drivers")
        .select("driver_name, is_active")
        .order("driver_name", { ascending: true });

      if (error) throw error;

      setDrivers(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error("Error fetching drivers:", e);
    }
  };

  useEffect(() => {
    fetchFobs();
    fetchDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedDriverNames]);

  // Filtered and sorted fobs
  const filteredAndSortedFobs = useMemo(() => {
    let result = [...fobs];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((fob) => {
        return (
          fob.fob_number.toLowerCase().includes(query) ||
          (fob.driver_name && fob.driver_name.toLowerCase().includes(query)) ||
          (fob.notes && fob.notes.toLowerCase().includes(query))
        );
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((fob) => fob.status === statusFilter);
    }

    // Apply assignment filter
    if (assignmentFilter === "assigned") {
      result = result.filter((fob) => fob.driver_name !== null && fob.driver_name !== "");
    } else if (assignmentFilter === "unassigned") {
      result = result.filter((fob) => fob.driver_name === null || fob.driver_name === "");
    }

    // Sort by fob_number using natural sort (numeric order)
    result.sort((a, b) => naturalSort(a.fob_number, b.fob_number));

    return result;
  }, [fobs, searchQuery, statusFilter, assignmentFilter]);

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setAssignmentFilter("all");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || statusFilter !== "all" || assignmentFilter !== "all";

  const resetAddForm = () => {
    setNewFob({
      fob_number: "",
      driver_name: "",
      date_assigned: "",
      deposit_paid: "",
      returned_date: "",
      deposit_refund: "",
      status: "active",
      notes: "",
    });
  };

  const addFob = async () => {
    if (!canManage) {
      toast({ title: "Not allowed", description: "Admin only.", variant: "destructive" });
      return;
    }

    if (!newFob.fob_number.trim()) {
      toast({
        title: "Missing fob number",
        description: "Please enter a fob number.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("key_fobs").insert({
        fob_number: newFob.fob_number.trim(),
        driver_name: newFob.driver_name.trim() || null,
        date_assigned: newFob.date_assigned || null,
        deposit_paid: newFob.deposit_paid || null,
        returned_date: newFob.returned_date || null,
        deposit_refund: newFob.deposit_refund || null,
        status: newFob.status,
        notes: newFob.notes.trim() || null,
      });

      if (error) throw error;

      toast({ title: "Key fob added", description: `Fob ${newFob.fob_number} created.` });
      setAddOpen(false);
      resetAddForm();
      fetchFobs();
    } catch (e: any) {
      toast({
        title: "Error adding key fob",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const openEdit = (fob: KeyFobRow) => {
    if (!canManage) return;
    setEditing(fob);
    setEditValues({
      fob_number: fob.fob_number,
      driver_name: fob.driver_name || "",
      date_assigned: fob.date_assigned ? fob.date_assigned.split("T")[0] : "",
      deposit_paid: (fob.deposit_paid as DepositPaidType) || "",
      returned_date: fob.returned_date ? fob.returned_date.split("T")[0] : "",
      deposit_refund: (fob.deposit_refund as DepositRefundType) || "",
      status: (fob.status as FobStatus) || "active",
      notes: fob.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!canManage || !editing) return;

    if (!editValues.fob_number.trim()) {
      toast({
        title: "Missing fob number",
        description: "Please enter a fob number.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("key_fobs")
        .update({
          fob_number: editValues.fob_number.trim(),
          driver_name: editValues.driver_name.trim() || null,
          date_assigned: editValues.date_assigned || null,
          deposit_paid: editValues.deposit_paid || null,
          returned_date: editValues.returned_date || null,
          deposit_refund: editValues.deposit_refund || null,
          status: editValues.status,
          notes: editValues.notes.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editing.id);

      if (error) throw error;

      toast({ title: "Key fob updated", description: `Fob ${editValues.fob_number} saved.` });
      setEditOpen(false);
      setEditing(null);
      fetchFobs();
    } catch (e: any) {
      toast({
        title: "Error updating key fob",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const deactivateFob = async (fob: KeyFobRow) => {
    if (!canManage) return;

    const newStatus = fob.status === "active" ? "inactive" : "active";
    const action = newStatus === "inactive" ? "deactivated" : "reactivated";

    try {
      const { error } = await supabase
        .from("key_fobs")
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", fob.id);

      if (error) throw error;

      toast({ title: `Key fob ${action}`, description: `Fob ${fob.fob_number} is now ${newStatus}.` });
      fetchFobs();
    } catch (e: any) {
      toast({
        title: "Error updating status",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  // Active drivers for dropdown
  const activeDrivers = useMemo(() => {
    return drivers.filter((d) => d.is_active);
  }, [drivers]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl font-semibold">Key Fobs</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchFobs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Fob
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search fobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as "all" | "active" | "inactive")}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={assignmentFilter} onValueChange={(val) => setAssignmentFilter(val as "all" | "assigned" | "unassigned")}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Assignment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fobs</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}

          {hasActiveFilters && (
            <span className="text-sm text-muted-foreground">
              Showing {filteredAndSortedFobs.length} of {fobs.length} fobs
            </span>
          )}
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fob Number</TableHead>
                <TableHead>Assigned Driver</TableHead>
                <TableHead>Date Assigned</TableHead>
                <TableHead>Deposit Paid</TableHead>
                <TableHead>Returned Date</TableHead>
                <TableHead>Deposit Refund</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    Loading key fobs...
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedFobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilters ? "No key fobs match the current filters." : "No key fobs found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedFobs.map((fob) => (
                  <TableRow key={fob.id}>
                    <TableCell className="font-medium">{fob.fob_number}</TableCell>
                    <TableCell>
                      {fob.driver_name ? (
                        <Badge variant="outline">{fob.driver_name}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(fob.date_assigned)}</TableCell>
                    <TableCell>
                      {fob.deposit_paid ? (
                        <Badge variant="secondary">{fob.deposit_paid}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(fob.returned_date)}</TableCell>
                    <TableCell>
                      {fob.deposit_refund ? (
                        <Badge variant="secondary">{fob.deposit_refund}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(fob.status)}>
                        {fob.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                      {fob.notes || "-"}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEdit(fob)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${fob.status === "active" ? "text-destructive hover:text-destructive" : "text-green-600 hover:text-green-600"}`}
                            onClick={() => deactivateFob(fob)}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Add Fob Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Key Fob</DialogTitle>
            <DialogDescription>
              Enter the details for the new key fob.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="fob_number">Fob Number *</Label>
              <Input
                id="fob_number"
                value={newFob.fob_number}
                onChange={(e) => setNewFob({ ...newFob, fob_number: e.target.value })}
                placeholder="e.g., FOB001"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="driver_name">Assign to Driver</Label>
              <Select
                value={newFob.driver_name || NO_DRIVER}
                onValueChange={(val) => setNewFob({ ...newFob, driver_name: val === NO_DRIVER ? "" : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>— Unassigned —</SelectItem>
                  {activeDrivers.map((d) => (
                    <SelectItem key={d.driver_name} value={d.driver_name}>
                      {d.driver_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="date_assigned">Date Assigned</Label>
              <Input
                id="date_assigned"
                type="date"
                value={newFob.date_assigned}
                onChange={(e) => setNewFob({ ...newFob, date_assigned: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deposit_paid">Deposit Paid</Label>
              <Select
                value={newFob.deposit_paid || NO_DRIVER}
                onValueChange={(val) => setNewFob({ ...newFob, deposit_paid: val === NO_DRIVER ? "" : val as DepositPaidType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>— None —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="EFTPOS">EFTPOS</SelectItem>
                  <SelectItem value="Deduction">Deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="returned_date">Returned Date</Label>
              <Input
                id="returned_date"
                type="date"
                value={newFob.returned_date}
                onChange={(e) => setNewFob({ ...newFob, returned_date: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deposit_refund">Deposit Refund</Label>
              <Select
                value={newFob.deposit_refund || NO_DRIVER}
                onValueChange={(val) => setNewFob({ ...newFob, deposit_refund: val === NO_DRIVER ? "" : val as DepositRefundType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select refund method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>— None —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Payment">Bank Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newFob.status}
                onValueChange={(val) => setNewFob({ ...newFob, status: val as FobStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newFob.notes}
                onChange={(e) => setNewFob({ ...newFob, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addFob}>Add Fob</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Fob Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Key Fob</DialogTitle>
            <DialogDescription>
              Update the key fob details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit_fob_number">Fob Number *</Label>
              <Input
                id="edit_fob_number"
                value={editValues.fob_number}
                onChange={(e) => setEditValues({ ...editValues, fob_number: e.target.value })}
                placeholder="e.g., FOB001"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_driver_name">Assign to Driver</Label>
              <Select
                value={editValues.driver_name || NO_DRIVER}
                onValueChange={(val) => setEditValues({ ...editValues, driver_name: val === NO_DRIVER ? "" : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>— Unassigned —</SelectItem>
                  {activeDrivers.map((d) => (
                    <SelectItem key={d.driver_name} value={d.driver_name}>
                      {d.driver_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_date_assigned">Date Assigned</Label>
              <Input
                id="edit_date_assigned"
                type="date"
                value={editValues.date_assigned}
                onChange={(e) => setEditValues({ ...editValues, date_assigned: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_deposit_paid">Deposit Paid</Label>
              <Select
                value={editValues.deposit_paid || NO_DRIVER}
                onValueChange={(val) => setEditValues({ ...editValues, deposit_paid: val === NO_DRIVER ? "" : val as DepositPaidType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>— None —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="EFTPOS">EFTPOS</SelectItem>
                  <SelectItem value="Deduction">Deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_returned_date">Returned Date</Label>
              <Input
                id="edit_returned_date"
                type="date"
                value={editValues.returned_date}
                onChange={(e) => setEditValues({ ...editValues, returned_date: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_deposit_refund">Deposit Refund</Label>
              <Select
                value={editValues.deposit_refund || NO_DRIVER}
                onValueChange={(val) => setEditValues({ ...editValues, deposit_refund: val === NO_DRIVER ? "" : val as DepositRefundType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select refund method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>— None —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Payment">Bank Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={editValues.status}
                onValueChange={(val) => setEditValues({ ...editValues, status: val as FobStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={editValues.notes}
                onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default KeyFobsTab;
